import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { hasInAppHistory } from '../hooks/useSmartBack';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import {
  AlertDialog, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  Upload, BookOpen, FileText,
  MessageCircle, X, Image as ImageIcon, Loader2,
  Eye, Edit3, ChevronDown, Check, AlertCircle, Search, AlignLeft
} from 'lucide-react';
import AppHeader from '../components/AppHeader';
import { getApiBase } from '../api/httpClient';
import { normalizeInternationalPhone } from '../utils/phone';
import './CreateBook.css';

const MAX_FILES = 5;
const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const CURRENCIES = [
  { value: 'SDG', en: 'Sudanese Pound', ar: '\u062c\u0646\u064a\u0647 \u0633\u0648\u062f\u0627\u0646\u064a' },
  { value: 'EGP', en: 'Egyptian Pound', ar: '\u062c\u0646\u064a\u0647 \u0645\u0635\u0631\u064a' },
  { value: 'SSP', en: 'South Sudanese Pound', ar: '\u062c\u0646\u064a\u0647 \u062c\u0646\u0648\u0628 \u0633\u0648\u062f\u0627\u0646\u064a' },
  { value: 'AED', en: 'UAE Dirham', ar: '\u062f\u0631\u0647\u0645 \u0625\u0645\u0627\u0631\u0627\u062a\u064a' },
  { value: 'SAR', en: 'Saudi Riyal', ar: '\u0631\u064a\u0627\u0644 \u0633\u0639\u0648\u062f\u064a' },
  { value: 'USD', en: 'US Dollar', ar: '\u062f\u0648\u0644\u0627\u0631 \u0623\u0645\u0631\u064a\u0643\u064a' },
  { value: 'KES', en: 'Kenyan Shilling', ar: '\u0634\u0644\u0646 \u0643\u064a\u0646\u064a' },
  { value: 'UGX', en: 'Ugandan Shilling', ar: '\u0634\u0644\u0646 \u0623\u0648\u063a\u0646\u062f\u064a' },
  { value: 'ETB', en: 'Ethiopian Birr', ar: '\u0628\u0631 \u0625\u062b\u064a\u0648\u0628\u064a' },
  { value: 'TZS', en: 'Tanzanian Shilling', ar: '\u0634\u0644\u0646 \u062a\u0646\u0632\u0627\u0646\u064a' },
  { value: 'MAD', en: 'Moroccan Dirham', ar: '\u062f\u0631\u0647\u0645 \u0645\u063a\u0631\u0628\u064a' },
];

const COUNTRY_CURRENCY_MAP = {
  sudan: 'SDG', egypt: 'EGP', south_sudan: 'SSP', uae: 'AED',
  saudi: 'SAR', kenya: 'KES', uganda: 'UGX', ethiopia: 'ETB',
  tanzania: 'TZS', morocco: 'MAD',
};

/* ── Localized helpers ── */
function getCurrencyDisplayName(code, isRTL) {
  const c = CURRENCIES.find(x => x.value === code);
  if (!c) return code;
  return isRTL ? c.ar : c.en;
}

function getCurrencyTriggerLabel(code, isRTL) {
  const c = CURRENCIES.find(x => x.value === code);
  if (!c) return code;
  return isRTL ? c.ar : `${c.value} - ${c.en}`;
}

function formatPrice(price, currencyCode, isRTL) {
  const c = CURRENCIES.find(x => x.value === currencyCode);
  const num = Number(price);
  if (isNaN(num)) return '';
  const formatted = num.toLocaleString(isRTL ? 'ar-SA' : 'en-US');
  if (!c) return `${currencyCode} ${formatted}`;
  return isRTL ? `${formatted} ${c.ar}` : `${c.value} ${formatted}`;
}

/* ── Custom Sheet Select ── */
function SheetSelect({ value, onChange, options, placeholder, error, testId }) {
  const [open, setOpen] = useState(false);
  const selected = options.find(o => o.value === value);
  const sheetRef = useRef(null);

  const handleSelect = useCallback((val) => {
    onChange(val);
    setOpen(false);
  }, [onChange]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (sheetRef.current && !sheetRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="cs-wrap" ref={sheetRef}>
      <button type="button" className={`cs-trigger ${error ? 'cb-input-error' : ''} ${value ? 'cs-has-value' : ''}`} onClick={() => setOpen(!open)} data-testid={testId}>
        <span className="cs-trigger-text">{selected ? selected.label : placeholder}</span>
        <ChevronDown size={16} className={`cs-chevron ${open ? 'cs-chevron-open' : ''}`} />
      </button>
      {open && (
        <div className="cs-panel" data-testid={`${testId}-panel`}>
          {options.map(opt => (
            <button key={opt.value} type="button" className={`cs-option ${opt.value === value ? 'cs-option-active' : ''}`} onClick={() => handleSelect(opt.value)} data-testid={`${testId}-opt-${opt.value}`}>
              <span className="cs-option-label">{opt.label}</span>
              {opt.value === value && <Check size={16} className="cs-check" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Currency Bottom Sheet ── */
function CurrencySheet({ open, onClose, value, onChange, isRTL }) {
  const [search, setSearch] = useState('');
  const searchRef = useRef(null);
  const overlayRef = useRef(null);

  const filtered = CURRENCIES.filter(c => {
    const q = search.toLowerCase();
    return c.value.toLowerCase().includes(q) || c.en.toLowerCase().includes(q) || c.ar.includes(search);
  });

  useEffect(() => {
    if (open && searchRef.current) setTimeout(() => searchRef.current?.focus(), 200);
    if (!open) setSearch('');
  }, [open]);

  if (!open) return null;

  return (
    <div className="cb-sheet-overlay" ref={overlayRef} onClick={(e) => { if (e.target === overlayRef.current) onClose(); }} data-testid="cb-currency-sheet">
      <div className="cb-sheet-panel" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="cb-sheet-header">
          <span className="cb-sheet-title">{isRTL ? '\u0627\u062e\u062a\u0631 \u0627\u0644\u0639\u0645\u0644\u0629' : 'Select Currency'}</span>
          <button type="button" className="cb-sheet-close" onClick={onClose} data-testid="cb-currency-sheet-close"><X className="h-5 w-5" /></button>
        </div>
        <div className="cb-sheet-search">
          <Search size={16} className="cb-sheet-search-icon" />
          <input ref={searchRef} type="text" className="cb-sheet-search-input" placeholder={isRTL ? '\u0627\u0628\u062d\u062b \u0639\u0646 \u0639\u0645\u0644\u0629...' : 'Search currency...'} value={search} onChange={e => setSearch(e.target.value)} data-testid="cb-currency-search" />
        </div>
        <div className="cb-sheet-list">
          {filtered.map(c => (
            <button key={c.value} type="button" className={`cb-sheet-option ${c.value === value ? 'cb-sheet-option-active' : ''}`} onClick={() => { onChange(c.value); onClose(); }} data-testid={`cb-currency-opt-${c.value}`}>
              <span className="cb-sheet-option-code">{c.value}</span>
              <span className="cb-sheet-option-name">{isRTL ? c.ar : c.en}</span>
              {c.value === value && <Check size={16} className="cb-sheet-check" />}
            </button>
          ))}
          {filtered.length === 0 && <p className="cb-sheet-empty">{isRTL ? '\u0644\u0627 \u062a\u0648\u062c\u062f \u0646\u062a\u0627\u0626\u062c' : 'No results'}</p>}
        </div>
      </div>
    </div>
  );
}

/* ── Main Component ── */
export default function CreateBook() {
  const { t, i18n } = useTranslation();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const lang = i18n.language || 'ar';
  const isRTL = lang === 'ar';
  const fileInputRef = useRef(null);
  const optionalTag = isRTL ? '(\u0627\u062e\u062a\u064a\u0627\u0631\u064a)' : '(Optional)';

  const fieldRefs = { title: useRef(null), author: useRef(null), subject_key: useRef(null), condition: useRef(null), mode: useRef(null), price: useRef(null), contact_method: useRef(null), phone: useRef(null), whatsapp: useRef(null), contact_email: useRef(null) };

  const [subjects, setSubjects] = useState([]);
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [errors, setErrors] = useState({});
  const [showValidationBanner, setShowValidationBanner] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showConfirmPublish, setShowConfirmPublish] = useState(false);
  const [showCurrencySheet, setShowCurrencySheet] = useState(false);
  const [images, setImages] = useState([]);

  const [form, setForm] = useState({
    title: '', author: '', subject_key: '', condition: '',
    price: '', currency_code: 'SDG', mode: 'sell',
    rent_unit: 'month',
    state_text: '', country: '', description: '',
    contact_methods: ['chat'], phone: '', whatsapp: '', contact_email: '',
    academic_level: '', content_type: '', university_text: '',
  });

  const phoneRequired = form.contact_methods.includes('phone');
  const whatsappSelected = form.contact_methods.includes('whatsapp');
  const emailSelected = form.contact_methods.includes('email');

  const toggleContactMethod = (method) => {
    setForm(prev => {
      const has = prev.contact_methods.includes(method);
      return { ...prev, contact_methods: has ? prev.contact_methods.filter(m => m !== method) : [...prev.contact_methods, method] };
    });
    setIsDirty(true);
    setErrors(prev => {
      const n = { ...prev };
      delete n.contact_method;
      if (method === 'phone') delete n.phone;
      if (method === 'whatsapp') delete n.whatsapp;
      if (method === 'email') delete n.contact_email;
      if (Object.keys(n).length === 0) setShowValidationBanner(false);
      return n;
    });
  };

  useEffect(() => {
    if (!user || !token) { navigate('/welcome'); return; }
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/student-hub/subjects`);
        if (res.ok) setSubjects(await res.json());
      } catch (e) { console.error(e); }
    })();
  }, [user, token, navigate]);

  useEffect(() => {
    return () => { images.forEach(img => { if (img.preview) URL.revokeObjectURL(img.preview); }); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const mapped = COUNTRY_CURRENCY_MAP[form.country];
    if (mapped) setForm(prev => ({ ...prev, currency_code: mapped }));
  }, [form.country]);

  const updateForm = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setIsDirty(true);
    setErrors(prev => {
      if (!prev[field]) return prev;
      const n = { ...prev };
      delete n[field];
      if (Object.keys(n).length === 0) setShowValidationBanner(false);
      return n;
    });
  };

  const handleImageChange = (e) => {
    const selected = Array.from(e.target.files || []);
    if (!selected.length) return;
    if (selected.length + images.length > MAX_FILES) { setErrors(p => ({ ...p, images: t('cb.maxImages') })); return; }
    for (const file of selected) {
      if (!ALLOWED_TYPES.includes(file.type)) { setErrors(p => ({ ...p, images: t('cb.invalidFormat') })); return; }
      if (file.size > MAX_FILE_SIZE) { setErrors(p => ({ ...p, images: t('cb.maxFileSize') })); return; }
    }
    setErrors(p => { const n = { ...p }; delete n.images; return n; });
    const prepared = selected.map(file => ({
      id: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`,
      file, preview: URL.createObjectURL(file),
    }));
    setImages(prev => [...prev, ...prepared]);
    setIsDirty(true);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeImage = (id) => {
    setImages(prev => {
      const target = prev.find(img => img.id === id);
      if (target?.preview) URL.revokeObjectURL(target.preview);
      return prev.filter(img => img.id !== id);
    });
    setIsDirty(true);
  };

  const handleBack = () => {
    if (isDirty) setShowExitDialog(true);
    else if (hasInAppHistory()) navigate(-1);
    else navigate('/student-hub/books', { replace: true });
  };

  const validateForm = () => {
    const errs = {};
    if (!form.title.trim()) errs.title = t('cb.errTitle');
    if (!form.author.trim()) errs.author = t('cb.errAuthor');
    if (!form.subject_key) errs.subject_key = t('cb.errSubject');
    if (!form.condition) errs.condition = t('cb.errCondition');
    if ((form.mode === 'sell' || form.mode === 'rent') && !String(form.price).trim()) errs.price = t('cb.errPrice');
    if (form.contact_methods.length === 0) errs.contact_method = t('cb.errContactMethod');
    if (phoneRequired) {
      if (!form.phone.trim()) errs.phone = t('cb.errPhone');
      else if (!normalizeInternationalPhone(form.phone.trim()).valid) errs.phone = t('cb.errPhoneInvalid');
    }
    if (whatsappSelected) {
      const wa = form.whatsapp.trim() || form.phone.trim();
      if (!wa) errs.whatsapp = t('cb.errWhatsapp');
      else if (!normalizeInternationalPhone(wa).valid) errs.whatsapp = t('cb.errWhatsappInvalid');
    }
    if (emailSelected) {
      const em = form.contact_email.trim();
      if (!em || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) errs.contact_email = t('cb.errEmail');
    }

    setErrors(errs);
    const hasErrors = Object.keys(errs).length > 0;
    if (hasErrors) {
      setShowValidationBanner(true);
      const order = ['title', 'author', 'subject_key', 'condition', 'price', 'contact_method', 'phone', 'whatsapp', 'contact_email'];
      for (const key of order) {
        if (errs[key] && fieldRefs[key]?.current) {
          fieldRefs[key].current.scrollIntoView({ behavior: 'smooth', block: 'center' });
          const input = fieldRefs[key].current.querySelector('input, textarea, button');
          if (input) setTimeout(() => input.focus(), 400);
          break;
        }
      }
    } else {
      setShowValidationBanner(false);
    }
    return !hasErrors;
  };

  const handlePreview = (e) => { e.preventDefault(); if (validateForm()) setShowPreview(true); };
  const handlePublishClick = () => setShowConfirmPublish(true);

  const handleConfirmPublish = async () => {
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      /* Upload images — skip if none */
      const uploadedUrls = [];
      for (const img of images) {
        if (!img.file) continue;
        const fd = new FormData();
        fd.append('files', img.file);
        let uploadRes;
        try {
          // iter282 — use getApiBase() so live wassla.tech hits
          // same-origin /api/upload; preview keeps the build-time URL.
          uploadRes = await fetch(`${getApiBase()}/api/upload`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: fd,
          });
        } catch (netErr) {
          console.error('[CreateBook] Upload network error:', netErr);
          toast.error(isRTL ? '\u062a\u0639\u0630\u0631 \u0631\u0641\u0639 \u0627\u0644\u0635\u0648\u0631\u0629. \u064a\u0631\u062c\u0649 \u0627\u0644\u0645\u062d\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062e\u0631\u0649.' : 'Unable to upload image. Please try again.');
          setIsSubmitting(false);
          return;
        }
        if (!uploadRes.ok) {
          let errMsg = isRTL ? '\u062a\u0639\u0630\u0631 \u0631\u0641\u0639 \u0627\u0644\u0635\u0648\u0631\u0629.' : 'Unable to upload image.';
          try { const errData = await uploadRes.json(); if (typeof errData.detail === 'string') errMsg = errData.detail; } catch {}
          toast.error(errMsg);
          setIsSubmitting(false);
          return;
        }
        let uploadData;
        try { uploadData = await uploadRes.json(); } catch { uploadData = {}; }
        const url = uploadData.images?.[0] || uploadData.url;
        if (url) uploadedUrls.push(url);
      }

      /* Build payload */
      const isFree = form.mode === 'free';
      const payload = {
        title: form.title.trim(),
        author: form.author.trim(),
        subject_key: form.subject_key,
        condition: form.condition,
        mode: form.mode,
        price: isFree ? 0 : (parseFloat(form.price) || 0),
        currency_code: isFree ? 'SDG' : form.currency_code,
        rent_unit: form.mode === 'rent' ? form.rent_unit : null,
        rent_price: null,
        academic_level: form.academic_level || null,
        content_type: form.content_type || null,
        state_key: form.state_text.trim() || null,
        country: form.country.trim() || 'sudan',
        university_key: form.university_text.trim() || null,
        description: form.description.trim() || null,
        contact_methods: form.contact_methods,
        phone: form.phone.trim() || null,
        whatsapp: whatsappSelected ? (form.whatsapp.trim() || null) : null,
        contact_email: emailSelected ? form.contact_email.trim() : null,
        images: uploadedUrls,
        preview_images: [],
      };

      let createRes;
      try {
        createRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/books`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } catch (netErr) {
        console.error('[CreateBook] Publish network error:', netErr);
        toast.error(isRTL ? '\u062a\u0639\u0630\u0631 \u0627\u0644\u0646\u0634\u0631. \u064a\u0631\u062c\u0649 \u0627\u0644\u0645\u062d\u0627\u0648\u0644\u0629 \u0645\u0631\u0629 \u0623\u062e\u0631\u0649.' : 'Unable to publish. Please try again.');
        setIsSubmitting(false);
        return;
      }

      if (createRes.ok) {
        const data = await createRes.json();
        setShowConfirmPublish(false);
        // iter293 — Invalidate the Student Hub items cache so the newly
        // published book appears IMMEDIATELY when the user navigates
        // back to /student-hub/books. Without this, a 5-min stale
        // cache served the old list and users reported "book published
        // but does not show in Books page".
        try {
          const mod = await import('../services/studentHubApi');
          if (typeof mod.clearCategoryCache === 'function') {
            mod.clearCategoryCache('books_notes');
          }
        } catch (_e) {
          // Best-effort — cache will expire after 5 min anyway.
        }
        toast.success(t('cb.success'));
        navigate(`/student-hub/books/${data.book_id}`);
      } else {
        let errMsg = t('cb.submitError');
        try {
          const errData = await createRes.json();
          if (typeof errData.detail === 'string') errMsg = errData.detail;
          else if (Array.isArray(errData.detail)) errMsg = errData.detail.map(d => d.msg).join(', ');
        } catch {}
        toast.error(typeof errMsg === 'string' ? errMsg : t('cb.submitError'));
        setIsSubmitting(false);
      }
    } catch (e) {
      console.error('[CreateBook] Unexpected error:', e);
      toast.error(t('cb.submitError'));
      setIsSubmitting(false);
    }
  };

  /* ── Option lists ── */
  const subjectOpts = subjects.map(s => ({ value: s.key, label: lang === 'ar' ? s.name_ar : s.name_en }));
  const conditionOpts = [
    { value: 'new', label: t('books.condition.new') },
    { value: 'used_good', label: t('cb.conditionUsedExcellent') },
    { value: 'used_ok', label: t('cb.conditionUsedGood') },
    { value: 'used_fair', label: t('cb.conditionUsedFair') },
  ];
  const levelOpts = [
    { value: 'primary', label: t('cb.levelPrimary') },
    { value: 'secondary', label: t('cb.levelSecondary') },
    { value: 'university', label: t('cb.levelUniversity') },
    { value: 'postgrad', label: t('cb.levelPostgrad') },
    { value: 'other', label: isRTL ? '\u0623\u062e\u0631\u0649' : 'Other' },
  ];
  // Posting-flow subtype list. The order + labels reflect the merchant
  // taxonomy the team converged on (Book / Study Summary / Notebook /
  // Exam Sheets / Lecture Notes / PDF / Magazine / Other). The underlying
  // value IDs are preserved/extended — existing ads with `notes`,
  // `slides`, `lab_manual`, `novels` still render correctly and remain
  // queryable; we just no longer surface those as new posting choices.
  const contentOpts = [
    { value: 'textbook',      label: t('cb.typeTextbook') },     // Book
    { value: 'summary',       label: t('cb.typeSummary') },      // Study Summary / ملخص
    { value: 'notebook',      label: t('cb.typeNotebook') },     // NEW — Notebook / دفتر
    { value: 'past_exam',     label: t('cb.typePastPapers') },   // Exam Sheets / نماذج
    { value: 'lecture_notes', label: t('cb.typeLectureNotes') }, // Lecture Notes / مذكرات
    { value: 'pdf',           label: t('cb.typePdf') },          // NEW — PDF / ملفات
    { value: 'magazine',      label: t('cb.typeMagazine') },     // NEW — Magazine / مجلة
    { value: 'other',         label: t('cb.typeOther') },
  ];
  const contactOpts = [
    { value: 'chat', label: t('cb.contactChat') },
    { value: 'phone', label: t('cb.contactPhone') },
    { value: 'whatsapp', label: t('cb.contactWhatsApp') },
    { value: 'email', label: t('cb.contactEmail') },
  ];

  const getLabelFromOpts = (val, opts) => opts.find(o => o.value === val)?.label || val || '\u2014';
  const showPriceCurrency = form.mode === 'sell' || form.mode === 'rent';

  /* ═══════════════ PREVIEW VIEW ═══════════════ */
  if (showPreview) {
    return (
      <div className="cb-page" dir={isRTL ? 'rtl' : 'ltr'} data-testid="cb-preview-page">
        <AppHeader
          title={t('cb.previewTitle')}
          subtitle={t('cb.previewSubtitle')}
          onBack={() => setShowPreview(false)}
          testId="cb-preview-header"
        />
        <div className="cb-container">

          <section className="cb-card" data-testid="cb-preview-card">
            {images.length > 0 && (
              <div className="cb-pv-images">
                {images.map(img => (
                  <div key={img.id} className="cb-pv-img-thumb"><img src={img.preview} alt="" /></div>
                ))}
              </div>
            )}
            <div className="cb-pv-grid" data-testid="cb-preview-details">
              <PvRow label={t('books.fields.title')} value={form.title} />
              <PvRow label={t('books.fields.author')} value={form.author} />
              <PvRow label={t('books.fields.subject')} value={getLabelFromOpts(form.subject_key, subjectOpts)} />
              <PvRow label={t('books.fields.condition')} value={getLabelFromOpts(form.condition, conditionOpts)} />
              <PvRow label={t('cb.listingType')} value={form.mode === 'sell' ? t('cb.modeSell') : form.mode === 'rent' ? t('cb.modeRent') : t('cb.modeFree')} />
              {showPriceCurrency && <PvRow label={t('books.fields.price')} value={formatPrice(form.price, form.currency_code, isRTL)} />}
              {form.academic_level && <PvRow label={t('cb.academicLevel')} value={getLabelFromOpts(form.academic_level, levelOpts)} />}
              {form.content_type && <PvRow label={t('cb.contentType')} value={getLabelFromOpts(form.content_type, contentOpts)} />}
              {form.country && <PvRow label={isRTL ? '\u0627\u0644\u062f\u0648\u0644\u0629' : 'Country'} value={form.country} />}
              {form.state_text && <PvRow label={isRTL ? '\u0627\u0644\u0648\u0644\u0627\u064a\u0629 / \u0627\u0644\u0645\u062f\u064a\u0646\u0629' : 'State / City'} value={form.state_text} />}
              {form.university_text && <PvRow label={t('books.fields.university')} value={form.university_text} />}
              <PvRow label={t('cb.preferredContact')} value={form.contact_methods.map(m => getLabelFromOpts(m, contactOpts)).join(' \u00b7 ') || '\u2014'} />
              {phoneRequired && form.phone && <PvRow label={t('phone')} value={form.phone} />}
              {whatsappSelected && (form.whatsapp || form.phone) && <PvRow label={t('cb.whatsappLabel')} value={form.whatsapp || form.phone} />}
              {emailSelected && form.contact_email && <PvRow label={t('cb.emailLabel')} value={form.contact_email} />}
            </div>
            {form.description && (
              <div className="cb-pv-desc" data-testid="cb-pv-description">
                <p className="cb-pv-desc-label">{t('books.fields.description')}</p>
                <p className="cb-pv-desc-text">{form.description}</p>
              </div>
            )}
          </section>

          <div className="cb-preview-actions" data-testid="cb-preview-actions">
            <button type="button" className="cb-edit-btn" onClick={() => setShowPreview(false)} data-testid="cb-preview-edit">
              <Edit3 className="h-4 w-4" />{t('cb.editBtn')}
            </button>
            <button type="button" className="cb-publish-btn" onClick={handlePublishClick} disabled={isSubmitting} data-testid="cb-publish-btn">
              {isSubmitting ? <Loader2 className="h-5 w-5 animate-spin" /> : t('cb.publishBtn')}
            </button>
          </div>
        </div>

        <AlertDialog open={showConfirmPublish} onOpenChange={(v) => { if (!isSubmitting) setShowConfirmPublish(v); }}>
          <AlertDialogContent dir={isRTL ? 'rtl' : 'ltr'} data-testid="cb-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>{t('cb.confirmTitle')}</AlertDialogTitle>
              <AlertDialogDescription>{t('cb.confirmMsg')}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isSubmitting} data-testid="cb-confirm-cancel">{t('exitCancel')}</AlertDialogCancel>
              <button type="button" className="cb-dialog-confirm-btn" onClick={handleConfirmPublish} disabled={isSubmitting} data-testid="cb-confirm-publish">
                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : t('cb.confirmPublish')}
              </button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    );
  }

  /* ═══════════════ FORM VIEW ═══════════════ */
  return (
    <div className="cb-page" dir={isRTL ? 'rtl' : 'ltr'} data-testid="create-book">
      <AppHeader
        title={t('cb.pageTitle')}
        subtitle={t('cb.pageSubtitle')}
        onBack={handleBack}
        testId="cb-header"
      />
      <div className="cb-container">
        {/* Validation Banner */}
        {showValidationBanner && Object.keys(errors).length > 0 && (
          <div className="cb-validation-banner" data-testid="cb-validation-banner">
            <AlertCircle className="h-5 w-5 cb-banner-icon" />
            <span className="cb-banner-text">{t('cb.validationBanner')}</span>
          </div>
        )}

        <form onSubmit={handlePreview}>
          {/* Images (Optional) */}
          <section className="cb-card" data-testid="cb-upload-section">
            <SectionHeader icon={<Upload className="h-5 w-5" />} title={`${t('cb.uploadTitle')} ${optionalTag}`} subtitle={t('cb.uploadSubtitle')} />
            {images.length > 0 && (
              <div className="cb-image-grid" data-testid="cb-image-previews">
                {images.map(img => (
                  <div key={img.id} className="cb-image-thumb">
                    <img src={img.preview} alt="" />
                    <button type="button" className="cb-image-remove" onClick={() => removeImage(img.id)}><X className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
              </div>
            )}
            {images.length < MAX_FILES && (
              <label className="cb-upload-zone" data-testid="cb-upload-zone">
                <input ref={fileInputRef} type="file" multiple accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleImageChange} />
                <div className="cb-upload-zone-inner">
                  <div className="cb-upload-icon-box"><ImageIcon className="h-6 w-6" /></div>
                  <p className="cb-upload-main-text">{t('cb.tapToUpload')}</p>
                  <p className="cb-upload-format-text">PNG, JPG, WEBP &middot; {t('cb.upTo5')} &middot; {t('cb.maxSize')}</p>
                </div>
              </label>
            )}
            {errors.images && <p className="cb-error-msg">{errors.images}</p>}
          </section>

          {/* Basic Details */}
          <section className="cb-card" data-testid="cb-basic-details">
            <SectionHeader icon={<BookOpen className="h-5 w-5" />} title={t('cb.basicTitle')} />
            <div className="cb-fields">
              <FieldGroup label={t('books.fields.title')} required error={errors.title} fieldRef={fieldRefs.title}>
                <input type="text" placeholder={t('cb.titlePlaceholder')} className={`cb-input ${errors.title ? 'cb-input-error' : ''}`} value={form.title} onChange={e => updateForm('title', e.target.value)} data-testid="cb-field-title" />
              </FieldGroup>
              <FieldGroup label={t('books.fields.author')} required error={errors.author} fieldRef={fieldRefs.author}>
                <input type="text" placeholder={t('cb.authorPlaceholder')} className={`cb-input ${errors.author ? 'cb-input-error' : ''}`} value={form.author} onChange={e => updateForm('author', e.target.value)} data-testid="cb-field-author" />
              </FieldGroup>
              <FieldGroup label={t('books.fields.subject')} required error={errors.subject_key} fieldRef={fieldRefs.subject_key}>
                <SheetSelect value={form.subject_key} onChange={v => updateForm('subject_key', v)} options={subjectOpts} placeholder={t('cb.selectSubject')} error={errors.subject_key} testId="cb-field-subject" />
              </FieldGroup>
              <FieldGroup label={t('books.fields.condition')} required error={errors.condition} fieldRef={fieldRefs.condition}>
                <SheetSelect value={form.condition} onChange={v => updateForm('condition', v)} options={conditionOpts} placeholder={t('cb.selectCondition')} error={errors.condition} testId="cb-field-condition" />
              </FieldGroup>
            </div>
          </section>

          {/* Listing Setup */}
          <section className="cb-card" data-testid="cb-listing-setup">
            <SectionHeader icon={<FileText className="h-5 w-5" />} title={t('cb.listingTitle')} />
            <div className="cb-fields">
              <FieldGroup label={t('cb.listingType')} required error={errors.mode} fieldRef={fieldRefs.mode}>
                <div className="cb-mode-grid" data-testid="cb-listing-type">
                  {[{ key: 'sell', label: t('cb.modeSell') }, { key: 'rent', label: t('cb.modeRent') }, { key: 'free', label: t('cb.modeFree') }].map(item => (
                    <button key={item.key} type="button" onClick={() => updateForm('mode', item.key)} className={`cb-mode-btn ${form.mode === item.key ? 'active' : ''}`} data-testid={`cb-mode-${item.key}`}>{item.label}</button>
                  ))}
                </div>
              </FieldGroup>

              {showPriceCurrency && (
                <>
                  <div className="cb-row">
                    <FieldGroup label={t('books.fields.price')} required error={errors.price} className="cb-flex-2" fieldRef={fieldRefs.price}>
                      <input type="number" placeholder="0" className={`cb-input ${errors.price ? 'cb-input-error' : ''}`} value={form.price} onChange={e => updateForm('price', e.target.value)} min="0" data-testid="cb-field-price" />
                    </FieldGroup>
                    <FieldGroup label={t('cb.currency')} className="cb-flex-1">
                      <button type="button" className="cs-trigger cs-has-value" onClick={() => setShowCurrencySheet(true)} data-testid="cb-field-currency">
                        <span className="cs-trigger-text">{getCurrencyTriggerLabel(form.currency_code, isRTL)}</span>
                        <ChevronDown size={16} className="cs-chevron" />
                      </button>
                    </FieldGroup>
                  </div>
                </>
              )}

              {form.mode === 'rent' && (
                <FieldGroup label={`${t('books.fields.rentUnit')} ${optionalTag}`}>
                  <SheetSelect value={form.rent_unit} onChange={v => updateForm('rent_unit', v)} options={[
                    { value: 'day', label: t('books.rentUnit.day') },
                    { value: 'week', label: t('books.rentUnit.week') },
                    { value: 'month', label: t('books.rentUnit.month') },
                  ]} placeholder={t('books.rentUnit.month')} testId="cb-field-rent-unit" />
                </FieldGroup>
              )}

              <FieldGroup label={`${t('cb.academicLevel')} ${optionalTag}`}>
                <SheetSelect value={form.academic_level} onChange={v => updateForm('academic_level', v)} options={levelOpts} placeholder={t('cb.selectLevel')} testId="cb-field-academic-level" />
              </FieldGroup>

              <FieldGroup label={`${t('cb.contentType')} ${optionalTag}`}>
                <SheetSelect value={form.content_type} onChange={v => updateForm('content_type', v)} options={contentOpts} placeholder={t('cb.selectType')} testId="cb-field-content-type" />
              </FieldGroup>
            </div>
          </section>

          {/* Description (Optional) */}
          <section className="cb-card" data-testid="cb-description-section">
            <SectionHeader icon={<AlignLeft className="h-5 w-5" />} title={`${t('books.fields.description')} ${optionalTag}`} />
            <div className="cb-fields">
              <FieldGroup label={t('cb.detailsLabel')}>
                <textarea rows={4} placeholder={t('cb.descPlaceholder')} className="cb-input cb-textarea" value={form.description} onChange={e => updateForm('description', e.target.value)} data-testid="cb-field-description" />
              </FieldGroup>
            </div>
          </section>

          {/* Location (Optional) */}
          <section className="cb-card" data-testid="cb-location-section">
            <SectionHeader icon={<BookOpen className="h-5 w-5" />} title={`${isRTL ? '\u0627\u0644\u0645\u0648\u0642\u0639' : 'Location'} ${optionalTag}`} />
            <div className="cb-fields">
              <FieldGroup label={`${isRTL ? '\u0627\u0644\u062f\u0648\u0644\u0629' : 'Country'} ${optionalTag}`}>
                <input type="text" placeholder={isRTL ? '\u0645\u062b\u0627\u0644: \u0627\u0644\u0633\u0648\u062f\u0627\u0646' : 'e.g. Sudan'} className="cb-input" value={form.country} onChange={e => updateForm('country', e.target.value)} data-testid="cb-field-country" />
              </FieldGroup>
              <FieldGroup label={`${isRTL ? '\u0627\u0644\u0648\u0644\u0627\u064a\u0629 / \u0627\u0644\u0645\u062f\u064a\u0646\u0629' : 'State / City'} ${optionalTag}`}>
                <input type="text" placeholder={isRTL ? '\u0645\u062b\u0627\u0644: \u0627\u0644\u062e\u0631\u0637\u0648\u0645' : 'e.g. Khartoum'} className="cb-input" value={form.state_text} onChange={e => updateForm('state_text', e.target.value)} data-testid="cb-field-state" />
              </FieldGroup>
              <FieldGroup label={`${t('books.fields.university')} ${optionalTag}`}>
                <input type="text" placeholder={isRTL ? '\u0645\u062b\u0627\u0644: \u062c\u0627\u0645\u0639\u0629 \u0627\u0644\u062e\u0631\u0637\u0648\u0645' : 'e.g. University of Khartoum'} className="cb-input" value={form.university_text} onChange={e => updateForm('university_text', e.target.value)} data-testid="cb-field-university" />
              </FieldGroup>
            </div>
          </section>

          {/* Contact Method */}
          <section className="cb-card" data-testid="cb-contact-section">
            <SectionHeader icon={<MessageCircle className="h-5 w-5" />} title={t('cb.contactTitle')} />
            <div className="cb-fields">
              <FieldGroup label={t('cb.preferredContact')} required error={errors.contact_method} fieldRef={fieldRefs.contact_method}>
                <div className="cb-mode-grid cb-contact-grid" data-testid="cb-contact-methods">
                  {contactOpts.map(opt => (
                    <button key={opt.value} type="button" onClick={() => toggleContactMethod(opt.value)} className={`cb-mode-btn ${form.contact_methods.includes(opt.value) ? 'active' : ''}`} data-testid={`cb-contact-${opt.value}`}>
                      {form.contact_methods.includes(opt.value) && <Check size={14} />}
                      {opt.label}
                    </button>
                  ))}
                </div>
                <p className="cb-contact-hint">{t('cb.contactHint')}</p>
              </FieldGroup>
              {phoneRequired && (
                <FieldGroup label={t('phone')} required error={errors.phone} fieldRef={fieldRefs.phone}>
                  <input type="tel" dir="ltr" placeholder="+249 xxx xxx xxx" className={`cb-input ${errors.phone ? 'cb-input-error' : ''}`} value={form.phone} onChange={e => updateForm('phone', e.target.value)} data-testid="cb-field-phone" />
                </FieldGroup>
              )}
              {whatsappSelected && (
                <FieldGroup label={t('cb.whatsappLabel')} required={!form.phone.trim()} error={errors.whatsapp} fieldRef={fieldRefs.whatsapp}>
                  <input type="tel" dir="ltr" placeholder="+249 xxx xxx xxx" className={`cb-input ${errors.whatsapp ? 'cb-input-error' : ''}`} value={form.whatsapp} onChange={e => updateForm('whatsapp', e.target.value)} data-testid="cb-field-whatsapp" />
                  {form.phone.trim() && !form.whatsapp.trim() && <p className="cb-contact-hint">{t('cb.whatsappFallbackHint')}</p>}
                </FieldGroup>
              )}
              {emailSelected && (
                <FieldGroup label={t('cb.emailLabel')} required error={errors.contact_email} fieldRef={fieldRefs.contact_email}>
                  <input type="email" dir="ltr" placeholder="name@example.com" className={`cb-input ${errors.contact_email ? 'cb-input-error' : ''}`} value={form.contact_email} onChange={e => updateForm('contact_email', e.target.value)} data-testid="cb-field-email" />
                </FieldGroup>
              )}
            </div>
          </section>

          {/* Preview CTA */}
          <div className="cb-submit-area" data-testid="cb-submit-section">
            <button type="submit" className="cb-submit-btn" data-testid="cb-preview-cta">
              <Eye className="h-5 w-5" />{t('cb.previewBtn')}
            </button>
            <p className="cb-submit-hint">{t('cb.submitHint')}</p>
          </div>
        </form>
      </div>

      {/* Currency Bottom Sheet */}
      <CurrencySheet open={showCurrencySheet} onClose={() => setShowCurrencySheet(false)} value={form.currency_code} onChange={v => updateForm('currency_code', v)} isRTL={isRTL} />

      {/* Exit Dialog */}
      <AlertDialog open={showExitDialog} onOpenChange={setShowExitDialog}>
        <AlertDialogContent dir={isRTL ? 'rtl' : 'ltr'}>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('exitTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('exitMessage')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('exitCancel')}</AlertDialogCancel>
            <button type="button" className="cb-dialog-confirm-btn" onClick={() => navigate('/student-hub/books')} data-testid="cb-exit-confirm">{t('exitConfirm')}</button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SectionHeader({ icon, title, subtitle }) {
  return (
    <div className="cb-section-header">
      <div className="cb-section-icon">{icon}</div>
      <div>
        <h2 className="cb-section-title">{title}</h2>
        {subtitle && <p className="cb-section-subtitle">{subtitle}</p>}
      </div>
    </div>
  );
}

function FieldGroup({ label, required, error, children, className = '', fieldRef }) {
  return (
    <div className={`cb-field-group ${className}`} ref={fieldRef}>
      <label className="cb-label">{label} {required && <span className="cb-required">*</span>}</label>
      {children}
      {error && <p className="cb-error-msg" data-testid="cb-field-error">{error}</p>}
    </div>
  );
}

function PvRow({ label, value }) {
  return (
    <div className="cb-pv-row">
      <span className="cb-pv-label">{label}</span>
      <span className="cb-pv-value">{value}</span>
    </div>
  );
}
